import { Stack, Chip } from '@mui/material';
import { VyIcon, type VyIconType } from '@vy/ui-design-system';
import { useCallback } from 'react';
import { useToggle } from '../../../../../hooks/use-toggle';
import { PhaseLabelChipIconButton } from './PhaseLabelChipIconButton';
import { EditablePhaseLabelChip } from './EditablePhaseLabelChip';
import { useUpsertPhaseLabel } from '../../../../../hooks/requests/use-phase-labels';
import { runMutation } from '../../../../../utils/vy-mutation';
import { isError } from '@vy/utils';
import { userMessage } from '../../../../../utils/vy-toasts';
import { PhaseType } from '@vy/types';

type PhaseLabelChipProps = {
  type: PhaseType;
  label: string;
  color: string;
  icon: VyIconType;
  backgroundColor: string;
  editable?: boolean;
};

export const PhaseLabelChip: React.FC<PhaseLabelChipProps> = ({
  type,
  label,
  color,
  icon,
  backgroundColor,
  editable = true,
}) => {
  const upsertPhaseLabelMutation = useUpsertPhaseLabel();
  const [hovering, toggleHovering] = useToggle(false);
  const [editingActive, toggleEditingActive] = useToggle(false);

  const handleMouseInteraction = useCallback(() => toggleHovering(), [toggleHovering]);

  const handleEditPhaseLabel = useCallback(() => {
    toggleEditingActive();
  }, [toggleEditingActive]);

  const savePhaseLabel = useCallback(
    (newLabel: string) => {
      void runMutation(
        upsertPhaseLabelMutation,
        {
          type,
          textLabel: newLabel,
        },
        {
          pending: 'Updating phase label...',
          success: 'Phase label updated',
          error: 'Failed to update phase label',
        },
        {
          onSuccess: () => toggleEditingActive(),
          onError: (error) => {
            if (isError(error)) {
              userMessage('error', error.message);
            }
          },
        },
      );
    },
    [toggleEditingActive, runMutation, upsertPhaseLabelMutation, type],
  );

  const canShowEditMode = editable && hovering;
  const editModeVisible = canShowEditMode && !editingActive;

  return (
    <Stack
      direction="row"
      onMouseEnter={handleMouseInteraction}
      onMouseLeave={handleMouseInteraction}
      sx={{
        alignItems: "center",
        width: "100%"
      }}>
      {editingActive ? (
        <EditablePhaseLabelChip
          label={label}
          color={color}
          icon={icon}
          backgroundColor={backgroundColor}
          onSubmit={savePhaseLabel}
          onDismiss={toggleEditingActive}
        />
      ) : (
        <Chip label={label} sx={{ backgroundColor, color }} icon={<VyIcon color={color} icon={icon} />} />
      )}
      {editModeVisible && <PhaseLabelChipIconButton icon="SmallPen" onClick={handleEditPhaseLabel} />}
    </Stack>
  );
};
